import {
  diffMergePatch,
  hasAuthCredentials,
  loadAccountConfigs,
  useAccountConfig,
  type JsonObject,
} from "@blpde/connect";
import {
  sanitizeNxCommandMemory,
  type NxCommandMemoryStore,
} from "@blpde/nx-ui";

export const PALETTE_MEMORY_CONFIG_KEY = "command_palette";

/**
 * Keeps the palette's „Zuletzt benutzt“ and pinned entries in the account
 * config key `command_palette` (`/v1/account/configs`, I-2510), so they follow
 * the account to every device. Saves are debounced and write only what
 * changed; a failed save keeps the in-memory state.
 */
export function createPaletteMemoryStore(
  options: { debounceMs?: number } = {},
): NxCommandMemoryStore {
  const config = useAccountConfig<JsonObject>(PALETTE_MEMORY_CONFIG_KEY, {
    immediate: false,
  });
  const debounce = options.debounceMs ?? 800;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    async load() {
      if (!hasAuthCredentials()) return null;
      await loadAccountConfigs([PALETTE_MEMORY_CONFIG_KEY]);
      if (!config.loaded.value) return null;
      return sanitizeNxCommandMemory(config.effective.value);
    },
    save(memory) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (!hasAuthCredentials()) return;
        const patch = diffMergePatch(
          config.effective.value,
          sanitizeNxCommandMemory(memory) as unknown as JsonObject,
        );
        if (Object.keys(patch).length)
          void config.patch(patch, { immediate: true });
      }, debounce);
    },
  };
}
